import { ParentProps, ValidComponent, createMemo, onCleanup, onMount, splitProps } from "solid-js";
import { DropdownTriggerProps } from "../st-dropdown/fragments/DropdownTrigger";
import { Dropdown, DropdownMenuProps } from "../..";
import { DropdownContentProps } from "../st-dropdown/fragments/DropdownContent";
import { requireDropdownContext } from "common";
import styles from './st-popover.module.css';

export default function Popover(props: ParentProps<DropdownMenuProps>) {
    return <Dropdown {...props}>
        {props.children}
    </Dropdown>
}

Popover.Trigger = function PopoverTrigger(props: ParentProps<DropdownTriggerProps>) {
    return <Dropdown.Trigger {...props} action={props.action ?? 'hover'} />
}

Popover.Content = function PopoverContent<T extends ValidComponent = 'div'>(props: ParentProps<DropdownContentProps<T>>) {
    const ctx = requireDropdownContext();
    const [local, other] = splitProps(props, ['children'])
    const placement = createMemo(() => ctx.placement() || 'bottom');

    let contentEl!: HTMLDivElement;
    let arrowEl!: HTMLDivElement;

    onMount(() => {
        const isOpen = ctx.isOpen();

        // the inner content is only valid when the dialog is shown.
        if (!isOpen || !contentEl) return;

        // Dialogs don't have an open event, so we need to monitor the open attribute to determine
        // when the dialog becomes visible
        const dialog = contentEl.closest('dialog') as HTMLDialogElement;
        var observer = new MutationObserver((mutations) => {
            for (const mut of mutations) {
                if (mut.attributeName === 'open' && dialog.open) {
                    dialog.blur();
                    reposition();
                }
            }
        });
        observer.observe(dialog, { attributes: true });

        onCleanup(() => {
            observer.disconnect();
        });

        dialog.classList.add(styles['popover-dialog']);
    });

    function reposition() {
        const dialog = contentEl.closest('dialog') as HTMLDialogElement;
        const open = ctx.isOpen();
        const x = ctx.offsetX();
        const y = ctx.offsetY();

        // Always center the arrow.  This works for 99% of cases, so doing a more intelligent positioning doesn't make sense.
        if (dialog && open && x > 0 && y > 0) {
            switch (ctx.placement()) {
                case 'bottom':
                case 'bottom-start':
                case 'bottom-end':
                case 'top':
                case 'top-start':
                case 'top-end':
                    arrowEl.style.top = '';
                    arrowEl.style.left = ((dialog.getBoundingClientRect().width - arrowEl.getBoundingClientRect().width) / 2) + 'px';
                    break;
                default:
                    arrowEl.style.top = ((dialog.getBoundingClientRect().height - arrowEl.getBoundingClientRect().height) / 2) + 'px';
                    arrowEl.style.left = '';
                    break;
            }
        }
    }

    return <Dropdown.Content {...other} ref={contentEl} class={styles.popover} data-placement={placement()}>
        <div ref={arrowEl} class={styles.arrow}></div>
        <div class="flexbox vertical">
            {local.children}
        </div>
    </Dropdown.Content>
}

Popover.defaultProps = new DropdownMenuProps();
